import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

export const CartDrawer = () => {
  const { items, updateQty, removeItem, clear, totalQty, subtotal, percentOff, discount, total, open, setOpen } = useCart();
  const navigate = useNavigate();

  const nudge =
    totalQty === 0 ? "Buy 2 prints and save 15% · buy 3 and save 25%"
      : totalQty === 1 ? "Add 1 more print to save 15% on your order"
        : totalQty === 2 ? "You're saving 15%! Add 1 more to save 25%"
          : "Nice — you're getting 25% off this order";

  const createOrder = async () => {
    const { data } = await api.post("/payments/create-order", {
      items: items.map((i) => ({ lookup_key: i.size_key, quantity: i.quantity, print_id: i.print_id, print_title: i.title })),
    });
    return data.order_id;
  };

  const onApprove = async (data) => {
    try {
      const { data: res } = await api.post(`/payments/capture/${data.orderID}`);
      if (res.payment_status === "paid") {
        clear();
        setOpen(false);
        navigate(`/payment/success?order_id=${data.orderID}`);
      } else {
        toast.error("Payment wasn't completed. Please try again.");
      }
    } catch {
      toast.error("Payment could not be completed.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} className="fixed inset-0 z-[70] bg-ink/60 backdrop-blur-sm" data-testid="cart-overlay" />
          <motion.aside data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[80] h-full w-full sm:w-[440px] bg-paper border-l border-line flex flex-col">
            <div className="flex items-center justify-between px-6 py-6 border-b border-line">
              <h2 className="font-serif text-3xl tracking-tight">Your cart</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="p-2 hover:text-brick transition-colors"><X size={22} /></button>
            </div>

            <div className="bg-ink text-paper label px-6 py-3 text-center">{nudge}</div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              <div className="px-6 py-4">
                {items.length === 0 ? (
                  <p className="text-stone mt-10 text-center">Your cart is empty.</p>
                ) : (
                  items.map((i) => (
                    <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-4 py-5 border-b border-line">
                      <img src={i.image} alt={i.title} className="w-20 h-24 object-cover border border-line" />
                      <div className="flex-1">
                        <h3 className="font-serif text-xl tracking-tight leading-tight">{i.title}</h3>
                        <span className="label text-stone">{i.dimensions}</span>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-line">
                            <button onClick={() => updateQty(i.key, i.quantity - 1)} data-testid={`cart-dec-${i.key}`} className="px-2 py-1.5 hover:bg-paper-2"><Minus size={14} /></button>
                            <span className="px-3 text-sm" data-testid={`cart-qty-${i.key}`}>{i.quantity}</span>
                            <button onClick={() => updateQty(i.key, i.quantity + 1)} data-testid={`cart-inc-${i.key}`} className="px-2 py-1.5 hover:bg-paper-2"><Plus size={14} /></button>
                          </div>
                          <span className="font-sans">${(i.price * i.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                      <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.key}`} className="text-stone hover:text-brick self-start"><X size={16} /></button>
                    </div>
                  ))
                )}
              </div>

              {items.length > 0 && (
                <div className="border-t border-line px-6 py-6 space-y-3">
                  <div className="flex justify-between text-stone"><span className="label">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  {percentOff > 0 && (
                    <div className="flex justify-between text-brick" data-testid="cart-discount">
                      <span className="label">Bundle discount ({percentOff}%)</span><span>−${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t border-line">
                    <span className="label text-ink">Total</span>
                    <span className="font-serif text-3xl" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                  <p className="label text-stone">Free shipping · pay securely with PayPal</p>
                  <div data-testid="paypal-buttons" className="pt-1 pb-2">
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48 }}
                      forceReRender={[total, items.length]}
                      createOrder={createOrder}
                      onApprove={onApprove}
                      onError={() => toast.error("PayPal ran into a problem. Please try again.")}
                    />
                  </div>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
